import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogIn } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when switching to desktop
  useEffect(() => {
    if (!isMobile && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [isMobile, mobileMenuOpen]);

  return (
    <header 
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'py-4 bg-white shadow-sm' : 'py-5 bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="w-full flex items-center justify-center">
          {/* Mobile Logo (visible only on mobile) */}
          {isMobile && (
            <div className="flex justify-between w-full items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src="/lovable-uploads/35a616d1-b039-454a-957c-5a01e892bffc.png" 
                  alt="Askspot" 
                  className="h-8 w-auto" 
                />
              </Link>
              
              {/* Mobile Menu Button */}
              <button 
                className="text-brand-primary p-2" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? (
                  <X size={24} />
                ) : (
                  <Menu size={24} />
                )}
              </button>
            </div>
          )}

          {/* Desktop Navigation with Logo and Login */}
          {!isMobile && (
            <div className="navbar-pill rounded-full px-2.5 py-2 flex items-center shadow-[0_4px_16px_rgba(0,0,0,0.08)] border border-gray-100 w-fit">
              <Link to="/" className="flex items-center mx-2">
                <img 
                  src="/lovable-uploads/35a616d1-b039-454a-957c-5a01e892bffc.png" 
                  alt="Askspot" 
                  className="h-7 w-auto" 
                />
              </Link>
              <NavLink to="/" label="Home" active={location.pathname === '/'} />
              <NavLink to="/features" label="Features" active={location.pathname === '/features'} />
              <NavLink to="/pricing" label="Pricing" active={location.pathname === '/pricing'} />
              <NavLink to="https://learn.askspot.ai" label="Courses" active={false} external={true} />
              <NavLink to="/book-demo" label="Book Demo" active={location.pathname === '/book-demo'} />
              <NavLink to="/wall-of-love" label="Wall of Love" active={location.pathname === '/wall-of-love'} />
              <NavLink to="/contact" label="Contact" active={location.pathname === '/contact'} />
              <NavLink to="https://app.askspot.ai" label="Login" active={location.pathname === '/trial'} icon={<LogIn size={16} className="mr-1.5" />} external={true} />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobile && mobileMenuOpen && (
        <div className="absolute top-full left-0 w-full bg-white shadow-lg rounded-b-2xl animate-fade-in-up">
          <div className="py-3 px-4 flex flex-col space-y-2">
            <MobileNavLink to="/" label="Home" active={location.pathname === '/'} onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/features" label="Features" active={location.pathname === '/features'} onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/pricing" label="Pricing" active={location.pathname === '/pricing'} onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="https://learn.askspot.ai" label="Courses" active={false} onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/book-demo" label="Book Demo" active={location.pathname === '/book-demo'} onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/wall-of-love" label="Wall of Love" active={location.pathname === '/wall-of-love'} onClick={() => setMobileMenuOpen(false)} />
            <MobileNavLink to="/contact" label="Contact" active={location.pathname === '/contact'} onClick={() => setMobileMenuOpen(false)} />
            
            <div className="pt-2 mt-2 border-t border-gray-100">
              <a 
                href="https://app.askspot.ai" 
                className="flex items-center w-full bg-[#003a70] text-white font-medium py-2.5 px-4 rounded-lg text-center transition-all duration-300"
                onClick={() => setMobileMenuOpen(false)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LogIn size={18} className="mr-2" />
                Login
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

interface NavLinkProps {
  to: string;
  label: string;
  active?: boolean;
  icon?: React.ReactNode;
  external?: boolean;
}

const NavLink = ({ to, label, active = false, icon, external = false }: NavLinkProps) => {
  const isLogin = label === "Login" || (external && label === "Login");
  
  return (
    isLogin || external ? (
      <a
        href={to}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${
          isLogin 
            ? 'bg-[#003a70] text-white hover:bg-[#003a70]/90 ml-1'
            : 'text-brand-gray hover:text-brand-dark'
        }`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {icon}
        {label}
      </a>
    ) : (
      <Link
        to={to}
        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${
          active 
            ? 'bg-[#FEE6D8] text-[#003a70] shadow-sm' 
            : 'text-brand-gray hover:text-brand-dark'
        }`}
      >
        {icon}
        {label}
      </Link>
    )
  );
};

interface MobileNavLinkProps extends NavLinkProps {
  onClick?: () => void;
}

const MobileNavLink = ({ to, label, active = false, onClick }: MobileNavLinkProps) => {
  const isExternal = to.startsWith('http');
  
  if (isExternal) {
    return (
      <a
        href={to}
        className={`px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${
          active 
            ? 'bg-[#FEE6D8] text-[#003a70]' 
            : 'text-brand-dark hover:bg-gray-50'
        }`}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }
  
  return (
    <Link
      to={to}
      className={`px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${
        active 
          ? 'bg-[#FEE6D8] text-[#003a70]' 
          : 'text-brand-dark hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      {label}
    </Link>
  );
};

export default Navbar;
